import { useState, useEffect, useCallback } from "react";
import useCyclePurchaseEffectApi from "@apis/useCyclePurchaseEffectApi";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import useMemberCyclePurchaseApi from "@apis/useMemberCyclePurchaseApi";
import { isEmpty, isFunction } from "@util/utils";
import useMessageDialog from "@util/hook/useMessageDialog";

/**
 * 循環訂單變更單資料取得 Hook
 * 負責處理主單、變更單、選項資料的取得邏輯
 *
 * @param {Object} options - 設定選項
 * @param {string|number} [options.cycleOrderID] - 主單ID
 * @param {string|number} [options.effectID=0] - 變更單ID（可選，用於編輯模式）
 * @param {boolean} [options.autoFetch=true] - 是否自動取得資料（預設 true）
 * @param {Function} [options.onFutureOrdersCheck] - 未執行變更單檢查的回調（可選）
 * @returns {Object} 資料狀態和取得函數
 */
const useCyclePurchaseEffectOrderData = ({ cycleOrderID, effectID = 0, autoFetch = true, onFutureOrdersCheck } = {}) => {
	const setMessageDialog = useMessageDialog();
	const { getCyclePurchaseEffectOrderApi } = useCyclePurchaseEffectApi();
	const { getCyclePurchaseMemberOrderApi, getCyclePurchaseOptionsApi } = useCyclePurchaseApi();
	const { getMemberCycleOrderFutureApi } = useMemberCyclePurchaseApi();

	// 主單資料
	const [cycleOrder, setCycleOrder] = useState({});

	// 變更單資料和選項資料（合併成一個 state，避免多次渲染）
	const [effectData, setEffectData] = useState({
		effectOrder: {},
		resource: {},
	});

	// 取得資料函數
	const fetchData = useCallback(
		async (orderID, effectIDValue) => {
			if (!orderID) return;

			try {
				// 1. 取得主單資料
				const orderResp = await getCyclePurchaseMemberOrderApi(orderID);
				if (orderResp) {
					setCycleOrder(orderResp);
				}

				// 2. 取得變更單資料（如果有 effectID）
				// 注意：getCyclePurchaseEffectOrderApi 回傳的 effectResp 包含 resource 欄位，但本身是變更單資料
				// 合併成一個 state 物件，避免多次渲染
				let hasEffectRespResource = false;

				if (Number(effectIDValue)) {
					const effectResp = await getCyclePurchaseEffectOrderApi(orderID, effectIDValue);
					if (effectResp) {
						// 需要將 resource 分離出來，其餘作為變更單資料
						if (effectResp.resource) {
							hasEffectRespResource = true;
							// 批次更新 effectOrder 和 resource，只觸發一次渲染
							const { resource: effectRespResource, ...effectOrderData } = effectResp;
							setEffectData({
								effectOrder: effectOrderData,
								resource: effectRespResource,
							});
						} else {
							// 如果沒有 resource，只更新 effectOrder
							setEffectData(prev => ({
								...prev,
								effectOrder: effectResp,
							}));
						}
					}
				}

				// 3. 取得選項資料（resource）
				// 如果 effectResp 中沒有 resource，則單獨取得
				if (!hasEffectRespResource) {
					const resourceResp = await getCyclePurchaseOptionsApi();
					if (resourceResp) {
						setEffectData(prev => ({
							...prev,
							resource: resourceResp,
						}));
					}
				}

				// 4. 檢查是否有未執行的變更單（需要 memberID）
				const memberID = orderResp?.memberID || 0;
				if (memberID) {
					const futureRes = await getMemberCycleOrderFutureApi(memberID, orderID);
					if (futureRes && !isEmpty(futureRes)) {
						isFunction(onFutureOrdersCheck) && onFutureOrdersCheck(futureRes);
						setMessageDialog({
							open: true,
							title: "提醒",
							msg: "該訂購單尚有未執行的變更單。",
						});
					}
				}
			} catch (error) {
				console.error("取得變更單資料失敗:", error);
			}
		},
		// eslint-disable-next-line
		[getCyclePurchaseMemberOrderApi, getCyclePurchaseEffectOrderApi, getCyclePurchaseOptionsApi, getMemberCycleOrderFutureApi, onFutureOrdersCheck, setMessageDialog],
	);

	// 自動取得資料
	useEffect(() => {
		if (autoFetch && cycleOrderID) {
			fetchData(cycleOrderID, effectID);
		}
		// eslint-disable-next-line
	}, [cycleOrderID, effectID, autoFetch]);

	return {
		// 資料
		cycleOrder,
		effectOrder: effectData.effectOrder,
		resource: effectData.resource,
		// 方法
		fetchData,
		// 便捷屬性
		memberID: cycleOrder?.memberID ?? 0,
		clubID: cycleOrder?.clubID ?? 0,
		shippingDay: cycleOrder?.shippingDay ?? 0,
	};
};

export default useCyclePurchaseEffectOrderData;
